import { generateId } from '@studio/models';
import {
  DeviceRegistryCommandFunctionType,
  DeviceRegistryCommandKey
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { Home } from './types.js';
import { locateProxy } from '#~/agent/index.js';
import { resolveDeviceAnalyticsContext } from '#~/analytics/device-context.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { track } from '#~/analytics/track.js';
import type { AnalyticsEvent } from '#~/analytics/types/index.js';
import { StudioError } from '#~/error/index.js';

/**
 * Move the specified device to its home position.
 *
 * @param deviceId - The ID of the device to be sent home.
 */
export const home: Home = async (deviceId, options) => {
  try {
    const proxy = locateProxy(deviceId);

    if (!proxy) {
      throw new Error('HM-K4E');
    }

    const deviceAnalyticsContext = resolveDeviceAnalyticsContext(deviceId) ?? {
      device_id: deviceId
    };

    void track({
      category: EVENT_CATEGORIES.DEVICE,
      name: EVENT_NAMES.DEVICE_MOVE_DIRECT,
      properties: {
        ...deviceAnalyticsContext,
        movement_type: 'ptz',
        shot_type: 'standard',
        tool_used: options?.toolUsed ?? 'home'
      }
    } as AnalyticsEvent);

    await proxy.mutate(
      {
        control: {
          device: {
            [deviceId]: {
              deviceSettings: {
                [DeviceRegistryCommandKey.MOVE_HOME]: {
                  action: {
                    action: DeviceRegistryCommandFunctionType.ACTION,
                    uuid: generateId()
                  }
                }
              }
            }
          }
        }
      },
      {}
    );
  } catch (cause) {
    throw new StudioError('Unable to move the device to its home position.', { cause });
  }
};
